import type { InviteTab, InviteTabKey } from '@tempmee/react-blocks';
import {
  fromFavorite,
  fromRecent,
  fromRecommended,
  type IListItemData,
} from '~/components/ShiftRequestRedesign/AddInvites/EmbeddedInviteList/professionalAdapters';
import { UserProfession, UserSubProfession } from '~/enums/UserProfession';

/**
 * The pool arithmetic behind the open shift's invite panel, kept free of React
 * so each step can be read — and tested — on its own. `useInvitePools` supplies
 * the data and owns the state; everything here is a pure function.
 */

/** The pools an office can invite *from*; `invited` is derived, not fetched. */
export type TSourceTabKey = Exclude<InviteTabKey, 'invited'>;

/** Pool captions, in the order the Figma lays the switcher out. */
export const TAB_LABELS: Record<InviteTabKey, string> = {
  favorites: 'Favorites',
  'worked-with': 'Worked with',
  'new-to-you': 'New to you',
  invited: 'Invited',
};

/** Everything that is already a profession or sub-profession code. */
const PROFESSION_CODES = new Set<string>([
  UserProfession.DN,
  UserProfession.DA,
  UserProfession.RDH,
  UserProfession.FD,
  UserSubProfession.EFDA,
]);

/**
 * The shift's profession as the code the pools are filtered by.
 *
 * `UserProfession.getCode` translates a display *name* ("Dental Hygienist") into
 * a code, which is what the post-a-shift and interview screens need — they hold
 * a name. But `job.profession` already holds the code, and translating a code
 * finds nothing and returns `undefined`, so `filterProfession` goes unset and
 * the client-side profession filter never runs.
 *
 * In practice the backend already scopes all three pools to the shift's
 * profession — measured against dev, favorites/worked-with/new-to-you came back
 * RDH-only for an RDH shift — so today this is a latent bug rather than a
 * visible one, and correcting it changes nothing on screen. It is still worth
 * correcting: the filter is the guarantee the app claims to make, and a pool
 * that ever arrives unscoped would otherwise offer the wrong profession
 * silently.
 *
 * Fixed here rather than in `getCode` or `EmbeddedInviteList`, because the other
 * callers genuinely do pass a name and the legacy list must keep behaving as it
 * does today (GOTU-6395 tracks doing the same on that side).
 */
export const resolveProfessionCode = (professionType: string): string | undefined =>
  PROFESSION_CODES.has(professionType) ? professionType : UserProfession.getCode(professionType);

/** The three raw pools as `useInviteProfessional` hands them over. */
export interface IRawPools {
  favorites: Parameters<typeof fromFavorite>[0][];
  'worked-with': Parameters<typeof fromRecent>[0][];
  'new-to-you': Parameters<typeof fromRecommended>[0][];
}

type TProfessionLookup = (code: string) => string;

/**
 * Adapts each raw pool into the shared invite-row shape, reusing the very
 * adapters the legacy list uses — notably so a "worked with" row keeps the id
 * `getRecentProfessionalId` derives, which is what the sent-invite set matches.
 */
export const toSourceRows = (
  raw: IRawPools,
  professionCode: string | undefined,
  getProfessionName: TProfessionLookup,
): Record<TSourceTabKey, IListItemData[]> => ({
  favorites: raw.favorites.map((pro) => fromFavorite(pro, professionCode, getProfessionName)),
  'worked-with': raw['worked-with'].map((pro) =>
    fromRecent(pro, professionCode, getProfessionName),
  ),
  'new-to-you': raw['new-to-you'].map((pro) =>
    fromRecommended(pro, professionCode, getProfessionName),
  ),
});

/**
 * `favoriteInvites[].professional` arrives as a bare id on the job payload but
 * as the populated professional elsewhere, so the ids are normalised rather than
 * trusted — the wrong shape would silently leave the Invited pool empty.
 */
export const normaliseInvitedIds = (sent: Iterable<unknown>): Set<string> =>
  new Set(
    [...sent]
      .map((invited) =>
        typeof invited === 'string' ? invited : ((invited as { id?: string })?.id ?? ''),
      )
      .filter(Boolean),
  );

/**
 * Moves every already-invited professional out of the pool they came from and
 * into `invited`.
 *
 * The move matters both ways: the backend silently refuses a second invite, and
 * the block gives the `invited` tab no checkboxes — so a professional left on a
 * selectable tab would offer an action that does nothing. Someone who appears in
 * more than one source pool is listed once, keeping the tab count honest.
 */
export const splitInvitedOut = (
  sourceRows: Record<TSourceTabKey, IListItemData[]>,
  invitedIds: ReadonlySet<string>,
): Record<InviteTabKey, IListItemData[]> => {
  const invited: IListItemData[] = [];
  const seen = new Set<string>();

  const withoutInvited = (rows: IListItemData[]) =>
    rows.filter((row) => {
      if (!invitedIds.has(row.id)) {
        return true;
      }
      if (!seen.has(row.id)) {
        seen.add(row.id);
        invited.push(row);
      }
      return false;
    });

  return {
    favorites: withoutInvited(sourceRows.favorites),
    'worked-with': withoutInvited(sourceRows['worked-with']),
    'new-to-you': withoutInvited(sourceRows['new-to-you']),
    invited,
  };
};

/**
 * The invited ids no source pool holds.
 *
 * The three pools answer "who *could* this office invite", which is not the
 * same question as "who *was* invited": a professional can be un-favorited,
 * rotate out of the recommendations, or mark the date unavailable after the
 * invite went out, and the backend drops them from the pool while the invite
 * lives on. Subtracting the pools would then lose the invite entirely — no row,
 * and a tab count short of the shift's real invites (GOTU-7381). These are the
 * ids the page has to fetch on its own before `splitInvitedOut` can place them.
 */
export const findUnpooledInvitedIds = (
  sourceRows: Record<TSourceTabKey, IListItemData[]>,
  invitedIds: ReadonlySet<string>,
): string[] => {
  const pooled = new Set(
    (Object.keys(sourceRows) as TSourceTabKey[]).flatMap((key) =>
      sourceRows[key].map((row) => row.id),
    ),
  );

  return [...invitedIds].filter((id) => !pooled.has(id));
};

/**
 * Adds professionals fetched by id to the invited pool.
 *
 * Appended rather than merged in invite order: the fetches resolve one by one,
 * so ordering by arrival would reshuffle the list under the office as each
 * lands. A row already placed by `splitInvitedOut` wins — the pool copy carries
 * the fields a profile cannot (real availability, the pool it came from).
 */
export const withHydratedInvited = (
  rowsByTab: Record<InviteTabKey, IListItemData[]>,
  hydrated: readonly IListItemData[],
): Record<InviteTabKey, IListItemData[]> => {
  const placed = new Set(rowsByTab.invited.map((row) => row.id));
  const extra = hydrated.filter((row) => !placed.has(row.id));

  if (extra.length === 0) {
    return rowsByTab;
  }

  return { ...rowsByTab, invited: [...rowsByTab.invited, ...extra] };
};

/** The switcher's tabs, each counting the pool behind it. */
export const toTabs = (rowsByTab: Record<InviteTabKey, IListItemData[]>): InviteTab[] =>
  (Object.keys(TAB_LABELS) as InviteTabKey[]).map((key) => ({
    key,
    label: TAB_LABELS[key],
    count: rowsByTab[key].length,
  }));

/**
 * Turns the block's "here is the full next selection" into the add/remove pair
 * the underlying toggle API takes.
 *
 * Removals are limited to rows the block can actually see: a professional
 * selected on another tab is absent from `next` simply because the block was
 * never told about them, and the running "N ADDED" count spans pools.
 */
export const diffSelection = (
  current: ReadonlySet<string>,
  next: readonly string[],
  visibleIds: ReadonlySet<string>,
): { added: string[]; removed: string[] } => {
  const nextIds = new Set(next);

  return {
    added: next.filter((id) => !current.has(id)),
    removed: [...current].filter((id) => visibleIds.has(id) && !nextIds.has(id)),
  };
};
